import { DndContext, DragOverlay } from "@dnd-kit/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { Toast } from "@smart/bridge-intake-components-dom";
import {
  aiFillSectionUri,
  CustomisedContactDetailsNS,
  SectionNS,
} from "@smart/bridge-types-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import { Tooltip } from "@smart/itops-sb-design-system-dom";
import { useProviderInfo } from "@smart/itops-smokeball-components-dom";
import {
  useDeleteCustomisedContactDetails,
  useLoadCustomisedContactDetails,
  useLoadInfoTrackSetupState,
  useQueryMatterFields,
  useUpdateCustomisedContactDetails,
} from "@smart/manage-gql-client-dom";

import { AIFillSection } from "./ai-fill-section";
import { useConditions } from "./condition/conditions";
import { EditConditionsModal } from "./condition/edit-conditions-modal";
import { DragOverlayDisplay } from "./drag-overlay-display";
import { useSectionItemOrder } from "./section-item-order";
import {
  baseLanguageKey,
  FormBuilderTabOptions,
  LanguageKey,
} from "../../../types";
import { FormLoadingStatus } from "../../create-form/creation-status";
import { DuplicatingFormModal } from "../../list-forms/duplicating";
import { EditingProvider } from "../editor";
import { useFormChanges, useUpdateData } from "../hooks";
import { FieldComponents, SectionNavigation } from "../navigation";
import { PublishFormPopup, usePublish } from "../publish-form";
import { Questions } from "../questions";
import { ContactDetailsModal } from "../questions/contact-details-modal";
import { GeneratingForm } from "../questions/generating-form";
import { MappedFieldsModal } from "../questions/mapped-fields-modal";
import { ChangeMappedFieldTypeModal } from "../questions/mapped-fields-modal/change-mapped-field-type-modal";
import { MoveFieldConfirmationModal } from "../questions/move-field-confirmation-modal";
import { SignatureTypeModal } from "../questions/signature/signature-type-modal";
import {
  resolveSignatureMethod,
  getSignatureTypeAvailability,
  setDefaultSignatureMethod,
} from "../questions/signature/utils";
import {
  EditableSection,
  FieldTemplate,
  FormInfo,
  GqlField,
  GqlFieldValues,
  GqlForm,
  GqlGroup,
  GqlMatterLayout,
  GqlMatterType,
  GqlSection,
  UpdateAIFillSettings,
} from "../types";
import { getOrderedSectionItems, toSectionField } from "../utils";

type EditViewProps = {
  formUri: string;
  form?: GqlForm | null;
  sections?: GqlSection[] | null;
  groups?: GqlGroup[] | null;
  fields?: GqlField[] | null;
  loading: {
    team: boolean;
    form: boolean;
    sections: boolean;
    groups: boolean;
    fields: boolean;
    matterTypes: boolean;
    matterLayouts: boolean;
    submissionLinkHashes: boolean;
  };
  loadingStatus: FormLoadingStatus;
  matterTypes?: GqlMatterType[];
  matterLayouts?: GqlMatterLayout[];
  loadMatterFields: ReturnType<typeof useQueryMatterFields>;
  createForm: () => Promise<void>;
  updateAIFillSettings: UpdateAIFillSettings;
  canUpdate: boolean;
  setTab: React.Dispatch<React.SetStateAction<FormBuilderTabOptions>>;
  teamForms?: FormInfo[];
  editFormLastSectionUri: [
    string | undefined,
    React.Dispatch<React.SetStateAction<string | undefined>>,
  ];
  setToasts?: (toasts: Toast[]) => void;
  viewingLanguage: LanguageKey;
  hasSubmissionLinks?: boolean;
};

const EditView = ({
  formUri,
  form,
  sections,
  fields,
  groups,
  loading,
  matterTypes,
  matterLayouts,
  loadingStatus,
  loadMatterFields,
  createForm,
  updateAIFillSettings,
  canUpdate,
  setTab,
  teamForms,
  editFormLastSectionUri,
  setToasts,
  viewingLanguage,
  hasSubmissionLinks,
}: EditViewProps) => {
  const newSectionUriRef = useRef(SectionNS.generateUri());
  const sectionsToUse: EditableSection[] = sections?.length
    ? sections
    : [{ uri: newSectionUriRef.current }];
  const firstSectionUri = form?.aiFillSettings?.allowAiFill
    ? aiFillSectionUri
    : sectionsToUse[0].uri;
  const [currentSectionUri, setCurrentSectionUri] = useState<string>(
    editFormLastSectionUri[0] || firstSectionUri,
  );
  const { setPublishModalOpen } = usePublish();
  const [selectedFieldToMap, setSelectedFieldToMap] = useState<
    GqlFieldValues | undefined
  >(undefined);
  const providerInfo = useProviderInfo();
  const {
    billingField,
    "LINK-3327": signatureField,
    "LINK-3588": digitalSignatureType,
    "LINK-3846": standardSignatureDisabled,
  } = useFeatureFlags();
  const signatureTypes = getSignatureTypeAvailability({
    digitalSignatureEnabled: digitalSignatureType,
    standardSignatureDisabled,
  });
  const signatureAvailable =
    signatureField && (signatureTypes.standard || signatureTypes.digital);
  const digitalOnlySignature =
    signatureTypes.digital && !signatureTypes.standard;
  const excludingFieldTemplates = [
    ...(billingField ? [] : [{ type: "payment" }]),
    ...(signatureAvailable ? [] : [{ type: "signature" }]),
  ].flat() as FieldTemplate[];

  const infoTrackSetupState = useLoadInfoTrackSetupState(
    {},
    {
      skip: !signatureAvailable || !digitalOnlySignature,
      fetchPolicy: "cache-first",
    },
  );

  const checkDigitalSignatureAvailability = useCallback(async () => {
    if (hasSubmissionLinks) {
      setToasts?.([
        {
          text: `Digital signature isn't available while this form has an active web ${loadLocale().terms.enquiry.name} link.`,
          palette: "warning",
          width: "medium",
        },
      ]);
      return false;
    }

    try {
      const authorized =
        infoTrackSetupState.result?.authorized ??
        (await infoTrackSetupState.refetch()).data?.infoTrackSetupState
          ?.authorized;

      if (authorized === true) return true;

      setToasts?.([
        {
          text: "Digital signature must be set up for your firm before adding this component.",
          palette: "warning",
          width: "medium",
        },
      ]);
    } catch (error) {
      console.error("Error checking digital signature setup: ", error);
      setToasts?.([
        {
          text: "We couldn't verify your firm's digital signature setup. Please try again.",
          palette: "warning",
          width: "medium",
        },
      ]);
    }
    return false;
  }, [
    hasSubmissionLinks,
    infoTrackSetupState.refetch,
    infoTrackSetupState.result?.authorized,
    setToasts,
  ]);

  const {
    editingCondition,
    setEditingCondition,
    checkHasConditions,
    getNumberOfConditions,
    conditionsMap,
    createLinksMap,
  } = useConditions({
    sections,
    groups,
    fields,
  });

  const sectionFields = fields?.filter(
    (f) => f.values.sectionUri === currentSectionUri,
  );

  const sectionGroups = groups?.filter(
    (g) => g.values.sectionUri === currentSectionUri,
  );

  const {
    highlightChanges,
    setHighlightChanges,
    sectionChangeCounts,
    sectionChangeEntries,
    effectiveFieldChangesMap,
    effectiveGroupChangesMap,
    effectiveSectionChangesMap,
    removedPublishedFields,
    removedPublishedGroups,
    removedPublishedSections,
  } = useFormChanges({
    formUri,
    form,
    sections,
    fields,
    groups,
    sectionsToUse,
    viewingLanguage,
  });

  const sectionChangeStatus = effectiveSectionChangesMap.get(currentSectionUri);
  const sectionHasUnpublishedChanges =
    highlightChanges && (sectionChangeCounts.get(currentSectionUri) ?? 0) > 0;

  const persistedSectionItems = useMemo(
    () =>
      getOrderedSectionItems({
        sectionUri: currentSectionUri,
        fields: [...(fields || []), ...removedPublishedFields],
        groups: [...(groups || []), ...removedPublishedGroups],
      }),
    [
      currentSectionUri,
      fields,
      groups,
      removedPublishedFields,
      removedPublishedGroups,
    ],
  );

  useEffect(() => {
    editFormLastSectionUri[1](currentSectionUri);
  }, [currentSectionUri]);

  const {
    addMappedFields,
    addGroup,
    addContactDetailsFields,
    updateGroup,
    updateExistingGroupForLayoutContact,
    changeGroupOrder,
    deleteGroup,
    addField,
    updateField,
    mergeUpdateField,
    mergeUpdateGroup,
    changeFieldOrder,
    deleteField,
    updateSection,
    updateFormSection,
    updateAllSections,
    duplicateGroup,
    newItemUri,
    setNewItemUri,
    signatureFieldUri,
    setSignatureFieldUri,
    pendingFields,
    updatingItems,
    updateFieldGroup,
    updateAIUserFeedback,
  } = useUpdateData({
    formUri,
    form,
    sectionFields,
    sectionGroups,
    createForm,
    currentSectionUri,
    setCurrentSectionUri,
    shouldCreateInitialSection: !sections?.length,
    viewingLanguage,
    digitalSignatureSupported: digitalSignatureType,
    standardSignatureDisabled,
    checkDigitalSignatureAvailability,
    hasSubmissionLinks,
  });

  const sectionItems = useMemo(() => {
    if (!pendingFields.length) return persistedSectionItems;
    const pendingUris = new Set(pendingFields.map((field) => field.uri));
    return getOrderedSectionItems({
      sectionUri: currentSectionUri,
      fields: [
        ...(fields || []).filter((field) => !pendingUris.has(field.uri)),
        ...removedPublishedFields,
        ...pendingFields.map(toSectionField),
      ],
      groups: [...(groups || []), ...removedPublishedGroups],
    });
  }, [
    currentSectionUri,
    fields,
    groups,
    pendingFields,
    persistedSectionItems,
    removedPublishedFields,
    removedPublishedGroups,
  ]);

  const {
    activeId,
    lastActiveIds,
    delta,
    questionEditMode,
    mappedFieldsOptions,
    setMappedFieldsOptions,
    contactDetailsOptions,
    setContactDetailsOptions,
    onDragStart,
    onDragMove,
    onDragOver,
    onDragEnd,
    pendingDropAction,
    cancelDropAction,
    confirmDropAction,
    sensors,
    sectionItemsToDisplay,
    collisionDetectionAlgorithm,
    dropAnimation,
  } = useSectionItemOrder({
    sectionFields,
    addField,
    addGroup,
    changeFieldOrder,
    changeGroupOrder,
    sectionItems,
    updateFieldGroup,
    fields,
    groups,
    mergeUpdateField,
    updateGroup,
    setToasts,
  });

  const { result: customisedContactDetails } = useLoadCustomisedContactDetails(
    {},
    {
      skip: !contactDetailsOptions,
      fetchPolicy: "cache-and-network",
    },
  );
  const [updateCustomisedContactDetails] = useUpdateCustomisedContactDetails();
  const [deleteCustomisedContactDetails] = useDeleteCustomisedContactDetails();

  useEffect(() => {
    if (
      sectionsToUse?.length &&
      !sectionsToUse.map((s) => s.uri).includes(currentSectionUri)
    ) {
      if (form?.aiFillSettings?.allowAiFill) {
        setCurrentSectionUri(aiFillSectionUri);
      } else {
        setCurrentSectionUri(sectionsToUse[0].uri);
      }
    }
  }, [sections?.length, formUri]);

  const isViewingBaseLanguage = viewingLanguage === baseLanguageKey;

  const isFormEmpty =
    !fields?.length && !groups?.length && sectionsToUse.length === 1;

  const isFormLoading =
    loadingStatus === "checking" || loadingStatus === "timedOut";
  const disabled =
    isFormLoading ||
    form?.aiUserFeedback?.status === "generating" ||
    form?.aiUserFeedback?.status === "generated" ||
    !isViewingBaseLanguage;
  const hasUnpublishedChanges =
    form?.rawForm.values.hasUnpublishedChanges ?? false;
  const showPublishPopup = hasUnpublishedChanges && canUpdate;

  return (
    <div className="flex flex-1 overflow-hidden">
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        collisionDetection={collisionDetectionAlgorithm}
      >
        <EditingProvider>
          <div className="overflow-y-auto border-neutral-100 border-r border-solid">
            <SectionNavigation
              form={form}
              sections={sectionsToUse}
              removedPublishedSections={removedPublishedSections}
              currentSectionUri={currentSectionUri}
              disabled={disabled}
              loading={loading}
              setCurrentSectionUri={setCurrentSectionUri}
              updateAllSections={updateAllSections}
              aiFillSettings={form?.aiFillSettings}
              updateAIFillSettings={updateAIFillSettings}
              viewingLanguage={viewingLanguage}
              sectionChangeCounts={sectionChangeCounts}
            />
          </div>
          <div className="flex flex-1 flex-col min-h-0 overflow-auto bg-neutral-50">
            <GeneratingForm
              updating={
                !!form?.aiUserFeedback && updatingItems[form.aiUserFeedback.uri]
              }
              title={form?.formTitle || ""}
              loadingStatus={loadingStatus}
              creationStatus={form?.creationStatus}
              aiUserFeedback={form?.aiUserFeedback}
              updateAIUserFeedback={updateAIUserFeedback}
              form={form}
            />
            {currentSectionUri === aiFillSectionUri ? (
              <AIFillSection
                aiFillSettings={form?.aiFillSettings}
                updateAIFillSettings={updateAIFillSettings}
                viewingLanguage={viewingLanguage}
                translations={form?.rawForm.values.translations}
                disabled={isFormLoading}
              />
            ) : (
              <Questions
                mode={questionEditMode}
                loading={loading}
                form={form}
                section={sectionsToUse.find((s) => s.uri === currentSectionUri)}
                sectionItems={sectionItemsToDisplay}
                editingItemUri={newItemUri}
                updatingItems={updatingItems}
                mergeUpdateField={mergeUpdateField}
                deleteField={deleteField}
                updateGroup={updateGroup}
                deleteGroup={deleteGroup}
                updateSection={updateFormSection}
                duplicateGroup={duplicateGroup}
                onUpdatingFieldDone={() => setNewItemUri(undefined)}
                matterLayouts={matterLayouts}
                matterTypes={matterTypes}
                loadMatterFields={loadMatterFields}
                loadingStatus={loadingStatus}
                conditionActions={{
                  checkHasConditions,
                  setEditingCondition,
                  getNumberOfConditions,
                }}
                setSelectedFieldToMap={setSelectedFieldToMap}
                setSignatureFieldUri={setSignatureFieldUri}
                checkDigitalSignatureAvailability={
                  checkDigitalSignatureAvailability
                }
                pendingFieldUris={pendingFields.map((field) => field.uri)}
                excludingFieldTemplates={excludingFieldTemplates}
                lastActiveIds={lastActiveIds}
                viewingLanguage={viewingLanguage}
                hasSubmissionLinks={hasSubmissionLinks}
                fieldChangesMap={effectiveFieldChangesMap}
                groupChangesMap={effectiveGroupChangesMap}
                sectionChangeStatus={sectionChangeStatus}
                sectionHasUnpublishedChanges={sectionHasUnpublishedChanges}
                isFormEmpty={isFormEmpty}
                formFields={fields || []}
                formGroups={groups || []}
                openContactDetailsModal={() =>
                  setContactDetailsOptions({
                    entryPoint: "Contact Details Shortcut",
                  })
                }
                openMappedFieldsModal={setMappedFieldsOptions}
              />
            )}
          </div>
          {showPublishPopup && (
            <div className="relative">
              <div className="absolute right-full bottom-[2rem] mr-[2rem]">
                <PublishFormPopup
                  onPublishClick={() => setPublishModalOpen(true)}
                  sectionChangeEntries={sectionChangeEntries}
                  highlightChanges={highlightChanges}
                  onHighlightChangesChange={setHighlightChanges}
                />
              </div>
            </div>
          )}
          <Tooltip
            text={
              isViewingBaseLanguage
                ? undefined
                : "To keep all languages in sync, any changes to the components must be made in the English version of the form."
            }
            placement="left-start"
            className="overflow-auto"
            offsetBy={10}
          >
            <FieldComponents
              disabled={
                disabled || loading.sections || loading.groups || loading.fields
              }
              excluding={excludingFieldTemplates}
            />
          </Tooltip>
          <ContactDetailsModal
            createFields={addContactDetailsFields}
            onClose={() => setContactDetailsOptions(undefined)}
            contactDetailsOptions={contactDetailsOptions}
            fields={fields || []}
            groups={groups || []}
            matterLayouts={matterLayouts}
            matterTypes={matterTypes}
            loadMatterFields={loadMatterFields}
            loading={loading.matterLayouts || loading.matterTypes}
            customisedContactDetails={(customisedContactDetails || []).map(
              (item) => ({
                uri: item.uri,
                name: item.name,
                roleName: item.roleName,
                contactType: item.contactType as "person" | "company",
                fields: item.fields.map((field) => ({
                  fieldName: field.fieldName,
                  label: field.label,
                })),
              }),
            )}
            onSaveCustomisedContactDetails={async ({
              uri: fieldSetUri,
              ...fieldSetInput
            }) => {
              const { data } = await updateCustomisedContactDetails({
                variables: {
                  uri: fieldSetUri || CustomisedContactDetailsNS.generateUri(),
                  fields: fieldSetInput,
                },
              });
              const saved = data?.updateCustomisedContactDetails;
              if (!saved) {
                throw new Error("Failed to save field set.");
              }
              return {
                uri: saved.uri,
                name: saved.name,
                roleName: saved.roleName,
                contactType: saved.contactType as "person" | "company",
                fields: saved.fields.map((field) => ({
                  fieldName: field.fieldName,
                  label: field.label,
                })),
              };
            }}
            onDeleteCustomisedContactDetails={async (uri) => {
              const { data } = await deleteCustomisedContactDetails({
                variables: { uri },
              });
              if (!data?.deleteCustomisedContactDetails) {
                throw new Error("Failed to delete field set.");
              }
            }}
          />
          <MappedFieldsModal
            createFields={addMappedFields}
            onClose={() => setMappedFieldsOptions(undefined)}
            matterLayouts={matterLayouts}
            mappedFieldsOptions={mappedFieldsOptions}
            matterTypes={matterTypes}
            loadMatterFields={loadMatterFields}
            fields={fields || []}
            groups={groups || []}
            loading={loading.matterLayouts || loading.matterTypes}
          />
          {digitalSignatureType && (
            <SignatureTypeModal
              open={!!signatureFieldUri}
              initialMethod={resolveSignatureMethod(
                sectionFields?.find((f) => f.uri === signatureFieldUri)?.values
                  .signature?.method,
              )}
              digitalDisabled={hasSubmissionLinks}
              standardSignatureDisabled={standardSignatureDisabled}
              onClose={() => setSignatureFieldUri(undefined)}
              onConfirm={async (method, setAsDefault) => {
                const field = sectionFields?.find(
                  (f) => f.uri === signatureFieldUri,
                )?.values;
                if (!field) {
                  setSignatureFieldUri(undefined);
                  return;
                }
                if (setAsDefault) {
                  setDefaultSignatureMethod(method);
                }
                if (
                  resolveSignatureMethod(field.signature?.method) !== method
                ) {
                  await mergeUpdateField({
                    current: field,
                    incoming: { signature: { method } },
                  });
                }
                setSignatureFieldUri(undefined);
              }}
            />
          )}
          {selectedFieldToMap && (
            <ChangeMappedFieldTypeModal
              updateField={updateField}
              updateExistingGroupForLayoutContact={
                updateExistingGroupForLayoutContact
              }
              onClose={() => setSelectedFieldToMap(undefined)}
              matterLayouts={matterLayouts}
              selectedFieldToMap={selectedFieldToMap}
              matterTypes={matterTypes}
              loadMatterFields={loadMatterFields}
              fields={fields || []}
              groups={groups || []}
              loading={loading.matterLayouts || loading.matterTypes}
            />
          )}
          <EditConditionsModal
            conditionsMap={conditionsMap}
            currentField={editingCondition}
            setCurrentField={setEditingCondition}
            mergeUpdateField={mergeUpdateField}
            mergeUpdateGroup={mergeUpdateGroup}
            updateSection={updateSection}
            createLinksMap={createLinksMap}
            fields={fields || []}
            groups={groups || []}
            sections={sections || []}
          />
          <DuplicatingFormModal
            duplicatingForm={canUpdate ? undefined : form}
            forms={teamForms || []}
            onClose={() => setTab("preview")}
            header={`${providerInfo?.label || "Smokeball"} forms cannot be edited`}
            description={`Duplicate this form to ${loadLocale().country === "US" ? "customize" : "customise"}`}
          />
          <MoveFieldConfirmationModal
            action={pendingDropAction}
            onCancel={cancelDropAction}
            onConfirm={confirmDropAction}
          />
          <DragOverlay dropAnimation={dropAnimation}>
            {activeId && (
              <DragOverlayDisplay
                activeId={activeId}
                delta={delta}
                sectionFields={sectionFields}
                sectionItems={sectionItems}
                questionEditMode={questionEditMode}
                matterTypes={matterTypes}
                matterLayouts={matterLayouts}
              />
            )}
          </DragOverlay>
        </EditingProvider>
      </DndContext>
    </div>
  );
};

export { EditView, EditViewProps };
